import { PlusOutlined } from "@ant-design/icons";
import { Button, Card, Checkbox, Col, Form, Input, InputNumber, Modal, Row, Select, Switch } from "antd";
import TextArea from "antd/lib/input/TextArea";
import CustomDatePicker from "common/CustomDatePicker";
import moment from "moment";
import { forwardRef, useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  BUYER_INQUIRY_ADD_ENDPOINT,
  BUYER_INQUIRY_EDIT_ENDPOINT,
  BUYER_INQUIRY_GET_COUNTRIES_ENDPOINT,
  BUYER_INQUIRY_LIST_ENDPOINT,
  GARMENT_CATEGORY_LIST_ENDPOINT,
  GARMENT_FOR_LIST_ENDPOINT,
  GARMENT_TYPE_LIST_ENDPOINT,
  GET_BUYING_OFFICE_ENDPOINT,
  GET_STYLE_LIST_ENDPOINT,
  GET_USERS_ENDPOINT,
  STYLE_ADD_DOC,
} from "../../../apiServices/API_ENDPOINTS";
import { getData, postData } from "../../../apiServices/common";
import HandleNotification from "../../../common/Notification";
import { commaSeparateNumber, isArrayAndHasValue } from "../../../utils/functions";
import DocumentUpload from "../../Common/DocumentUpload";
import BuyingOfficeForm from "../CreateModalUI/BuyingOfficeForm";
import { FABRIC_TYPE, ORDER_CHANNEL, ORDER_TYPE, disableSelectedProcess } from "../helper";
import Reference from "../Reference";
import MachineRentTable from "../SpecialProcess/MachineRentTable";
import SpecialProcessTable from "../SpecialProcess/SpecialProcessTable";
import CMForm from "./CMForm";
import OBForm from "./OBForm";
import OrderConfirmationForm from "./OrderConfirmationForm";

const inquiryStatus = [
  {
    id: 1,
    name: "Inquiry",
    value: "0",
  },
  {
    id: 2,
    name: "Contracted",
    value: "1",
  },
  {
    id: 3,
    name: "Closed",
    value: "2",
  },
  {
    id: 4,
    name: "Capacity Booking",
    value: "3",
  },
];

const BuyerInquiryForm = forwardRef((props, ref) => {
  // Props
  const {
    form,
    isEdit,
    editInfo,
    fileData,
    CMInfo,
    viewMode,
    users,
    setUsers,
    setStyles,
    buyingOffice,
    setBuyingOffice,
    buyer,
    setBuyer,
    brand,
    setBrand,
    garmentCategoryList,
    setGarmentCategoryList,
    garmentTypeList,
    setGarmentTypeList,
    garmentForList,
    setGarmentForList,
    countryList,
    setCountryList,
    documents,
    editMode,
    setIsLoading,
  } = props;

  // States
  const [filesList, setFileList] = useState([]);
  const [selectedBuyingOffice, setSelectedBuyingOffice] = useState(null);
  const [selectedBuyer, setSelectedBuyer] = useState(null);
  const [subcontractStatusOption, setSubcontractStatusOption] = useState([]);
  const [specialProcessList, setSpecialProcessList] = useState(null);
  const [machineRentList, setMachineRentList] = useState(null);
  const [inquiryReferences, setInquiryReferences] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedModal, setSelectedModal] = useState(null);

  // Others
  const navigate = useNavigate();
  const { Option } = Select;
  const { inquiryId } = useParams();
  const buying_office_id_watch = Form.useWatch("buying_office_id", form);
  const buyer_id_watch = Form.useWatch("buyer_id", form);
  const inquiry_status_watch = Form.useWatch("inquiry_status", form);
  const subcontract_status = Form.useWatch("subcontract_status", form);

  useEffect(() => {
    // When in Edit Mode, set fileList values
    if (isArrayAndHasValue(fileData)) {
      const fileInfo = fileData?.map((file) => {
        return {
          id: file?.id,
          name: file?.document_name,
          path: file?.document,
          attachment_type: file?.attachment_type,
          reference_no: file?.reference_no,
          is_delete: 0,
        };
      });
      setFileList(fileInfo);
    }
  }, [fileData]);

  const onFinish = async (values) => {
    setIsLoading(true);

    const addQuery = `${BUYER_INQUIRY_ADD_ENDPOINT}`;
    const editQuery = `${BUYER_INQUIRY_EDIT_ENDPOINT}/${inquiryId}`;
    const query = isEdit ? editQuery : addQuery;

    const sub_contract = values.subcontract_status ? {} : {};

    const bodyData = {
      ...sub_contract,
      buying_office_id: values?.buying_office_id ? Number(values?.buying_office_id) : null,
      buyer_id: values?.buyer_id ? Number(values?.buyer_id) : null,
      brand_id: values?.brand_id,
      received_date: values?.received_date ? moment(values?.received_date).format("YYYY-MM-DD") : null,
      request_date: values?.request_date ? moment(values?.request_date).format("YYYY-MM-DD") : null,
      received_by: values?.received_by,
      inquiry_no: values?.inquiry_no,
      garment_category_id: values?.garment_category_id,
      garment_type_id: values?.garment_type_id,
      garment_for_id: values?.garment_for_id,
      inquiry_status: values?.inquiry_status,
      description: values?.description,
      order_status: values?.order_status,
      season: values?.season,
      size_range: values?.size_range,
      expected_order_quantity: values?.expected_order_quantity ? Number(values?.expected_order_quantity) : null,
      order_quantity: values?.order_quantity ? Number(values?.order_quantity) : null,
      extra_percent: values?.extra_percent ? Number(values?.extra_percent) : 0,
      expected_delivery_date: values?.expected_delivery_date
        ? moment(values?.expected_delivery_date).format("YYYY-MM-DD")
        : null,
      production_month_from: values?.production_month_from
        ? moment(values?.production_month_from).format("YYYY-MM-DD")
        : null,
      production_month_to: values?.production_month_to
        ? moment(values?.production_month_to).format("YYYY-MM-DD")
        : null,
      documents: isArrayAndHasValue(filesList)
        ? filesList?.map((file) => {
            return {
              document_id: file?.id,
              attachment_type: file?.attachment_type || null,
              reference_no: file?.reference_no || null,
              is_delete: file?.is_delete ? 1 : 0,
            };
          })
        : [],
      order_confirmations: isArrayAndHasValue(values?.order_confirmations)
        ? values?.order_confirmations?.map((item) => {
            return {
              id: item?.id || 0,
              inquiry_reason_id: item?.inquiry_reason_id || null,
              inquiry_remark_id: item?.inquiry_remark_id || null,
              is_delete: item?.is_delete || 0,
            };
          })
        : [],
      expected_margin: values?.expected_margin,
      buyer_target: values?.buyer_target,
      subcontract_status: values?.subcontract_status ? 1 : 0,
      sub_contract_factory_id: values?.sub_contract_factory_id,
      order_type: values?.order_type || null,
      order_channel: values?.order_channel || null,
      fabric_content: values?.fabric_content || null,
      fob: values?.fob || null,
      capacity_availability: values?.capacity_availability || null,
      country_id: values?.country_id ? Number(values?.country_id) : null,
      embroidery: values?.embroidery ? "Yes" : "No",
      printing: values?.printing ? "Yes" : "No",
      washing: values?.washing ? "Yes" : "No",
      smoking: values?.smoking ? "Yes" : "No",
      pintuck: values?.pintuck ? "Yes" : "No",
      snap_button: values?.snap_button ? "Yes" : "No",
      fabric_status: values?.fabric_status,
      trim_status: values?.trim_status,
      accessories_status: values?.accessories_status,
      reference_id: values?.reference_id,
      style_id: values?.style_id || null,
    };

    const response = isEdit ? await postData(query, bodyData) : await postData(query, bodyData);

    if (response && response?.code === 200) {
      HandleNotification(
        "success",
        "Success!",
        response?.message || `Successfully ${isEdit ? "updated" : "added"} inquiry`,
      );

      if (values.order_status === "Yes") {
        navigate("/production-planning/planningRequests/create");
      } else {
        navigate("/merchandising/capacity-booking");
      }
      setIsLoading(false);
    } else {
      HandleNotification("error", "Error!", response?.message || `Failed to ${isEdit ? "update" : "add"} inquiry`);
      setIsLoading(false);
    }
  };

  const getAllUsers = async (filterValues) => {
    const bodyData = {
      per_page: filterValues?.per_page || 20,
      ...filterValues,
    };
    const query = `${GET_USERS_ENDPOINT}`;

    const response = await getData(query, false, bodyData);

    if (response && response?.data?.code === 200) {
      const userId = JSON.parse(localStorage.getItem("user_id"));
      const userName = localStorage.getItem("name");

      const userData = response?.data?.data?.data || [];

      const currentUserExists = userData.some((user) => user.id === userId);

      if (!currentUserExists && userId && userName) {
        userData.unshift({
          id: userId,
          name: userName,
        });
      }

      setUsers(userData);

      if (!isEdit && !form.getFieldValue("received_by")) {
        form.setFieldsValue({
          received_by: userId,
          received_by_name: userId,
        });
      }
    }
  };

  const getAllStyles = useCallback(
    async (filterValues) => {
      const bodyData = {
        per_page: filterValues?.per_page || 20,
        ...filterValues,
      };
      const query = `${GET_STYLE_LIST_ENDPOINT}`;

      const response = await getData(query, false, bodyData);

      if (response && response?.data?.code === 200) {
        setStyles(response?.data?.data?.data);
      } else {
        HandleNotification("error", "Error!", response?.message || `Failed to get style list`);
      }
    },
    [setStyles],
  );

  const getAllGarmentCategory = async (filterValues) => {
    const bodyData = {
      per_page: filterValues?.per_page || 20,
      ...filterValues,
    };
    const query = `${GARMENT_CATEGORY_LIST_ENDPOINT}`;

    const response = await getData(query, false, bodyData);

    if (response && response?.data?.code === 200) {
      setGarmentCategoryList(response?.data?.data?.data);
    }
  };

  const getAllGarmentType = async (filterValues) => {
    const bodyData = {
      per_page: filterValues?.per_page || 20,
      ...filterValues,
    };
    const query = `${GARMENT_TYPE_LIST_ENDPOINT}`;

    const response = await getData(query, false, bodyData);

    if (response && response?.data?.code === 200) {
      setGarmentTypeList(response?.data?.data?.data);
    }
  };

  const getAllGarmentFor = async (filterValues) => {
    const bodyData = {
      per_page: filterValues?.per_page || 20,
      ...filterValues,
    };
    const query = `${GARMENT_FOR_LIST_ENDPOINT}`;

    const response = await getData(query, false, bodyData);

    if (response && response?.data?.code === 200) {
      setGarmentForList(response?.data?.data?.data);
    }
  };

  const getBuyingOfficeList = async (filterValues) => {
    const query = `${GET_BUYING_OFFICE_ENDPOINT}`;
    const bodyData = {
      per_page: 10,
      name: filterValues?.name || "",
    };
    const response = await getData(query, false, bodyData);

    if (response && response?.data?.code === 200) {
      setBuyingOffice(response?.data?.data?.data || []);
    }
  };

  const getBuyersList = async (value, filterValues) => {
    if (!filterValues) {
      form.resetFields(["buyer_id", "brand_id"]);
    }

    const query = `/api/buying_office/${value}/edit`;
    const bodyData = {
      ...(filterValues?.name && { name: filterValues?.name }),
      per_page: 10,
    };
    const response = await getData(query, false, bodyData);

    if (response && response?.data?.code === 200) {
      setBuyer(response?.data?.data?.buyer || []);
    }
  };

  const getBrand = async (value, filterValues) => {
    if (!filterValues) {
      form.resetFields(["brand_id"]);
    }

    // Set buyer short name field value
    const foundBuyer = buyer?.find((item) => item?.id === value);
    form.setFieldsValue({
      buyer_short_name: foundBuyer?.short_name,
    });

    const bodyData = {
      ...(filterValues?.name && { name: filterValues?.name }),
      per_page: 10,
    };
    const response = await getData(`/api/buyers/${value}/edit`, false, bodyData);

    if (response && response?.data?.code === 200) {
      setBrand(response?.data?.data?.brands);
    }
  };

  const getSubcontractStatusOption = async (filterValues) => {
    const bodyData = {
      ...(filterValues?.name && { name: filterValues?.name }),
      per_page: 10,
    };
    const response = await getData(`/api/sub-contract-factory`, false, bodyData);

    if (response && response?.data?.code === 200) {
      setSubcontractStatusOption(response?.data?.data?.data);
    }
  };

  const getCountries = async (filterValues) => {
    const query = BUYER_INQUIRY_GET_COUNTRIES_ENDPOINT;
    const bodyData = {
      per_page: 500,
      status: "active",
      ...(filterValues?.name && { name: filterValues.name }),
    };
    const response = await getData(query, false, bodyData);

    if (response && response?.data?.code === 200) {
      setCountryList(response?.data?.data?.data || []);
    }
  };

  useEffect(() => {
    getAllStyles({ subcontract_status: subcontract_status ? 1 : 0 });
  }, [subcontract_status, getAllStyles]);

  useEffect(() => {
    form.setFieldsValue({
      inquiry_status: "3", // Set to "Capacity Booking" by default
      request_date: moment(),
    });
    // Get all data on first load
    getAllUsers();
    getAllGarmentCategory();
    getAllGarmentType();
    getAllGarmentFor();
    getBuyingOfficeList();
    getSubcontractStatusOption();
    getProcessMachineData();
    getCountries();
  }, []);

  useEffect(() => {
    // In edit mode set buyer, if buying office is selected
    if (isEdit && buying_office_id_watch) {
      getBuyersList(buying_office_id_watch, {});
      setSelectedBuyingOffice(buying_office_id_watch);
    }
  }, [buying_office_id_watch, isEdit]);

  useEffect(() => {
    // In edit mode set brand, if buyer is selected
    if (isEdit && buyer_id_watch) {
      getBrand(buyer_id_watch, {});
      setSelectedBuyer(buyer_id_watch);
    }
  }, [buyer_id_watch, isEdit]);

  const getProcessMachineData = useCallback(
    async (filterValues) => {
      const bodyData = {
        ...filterValues,
        per_page: filterValues?.per_page || 10,
      };
      const query = `${BUYER_INQUIRY_LIST_ENDPOINT}/${inquiryId}/view-process-machine-rent`;
      const response = await getData(query, false, bodyData);

      if (response && response?.data?.code === 200) {
        const processData = isArrayAndHasValue(response?.data?.data?.process_info)
          ? response?.data?.data?.process_info
          : null;
        const customProcessData =
          processData &&
          processData?.map((item, index) => {
            // Disable the once used process
            disableSelectedProcess(item?.process);

            // Return custom data
            return {
              ...item,
              is_delete: 0,
              unique_id: index,
            };
          });
        const machineData = isArrayAndHasValue(response?.data?.data?.machine_info)
          ? response?.data?.data?.machine_info
          : null;
        const customMachineData =
          machineData &&
          machineData?.map((item, index) => {
            return {
              ...item,
              is_delete: 0,
              unique_id: index,
            };
          });
        setSpecialProcessList(customProcessData);
        setMachineRentList(customMachineData);
      } else {
        HandleNotification("error", "bottomRight", "Error getting process and machine data", null);
      }
    },
    [inquiryId],
  );

  const onModalOpen = (props) => {
    const { modalName } = props;
    setSelectedModal(modalName);
    setIsModalOpen(true);
  };

  const onCloseModal = () => {
    setIsModalOpen(false);
    setSelectedModal(null);
  };

  const renderModalTitle = () => {
    switch (selectedModal) {
      case "buying_office":
        return "Buying Office";
      case "buyer":
        return "Buyer";
      case "fabric_type":
        return "Fabric Type";
      default:
        return "";
    }
  };

  const renderModalUI = () => {
    switch (selectedModal) {
      case "buying_office":
        return <BuyingOfficeForm onCloseModal={onCloseModal} refetch={getBuyingOfficeList} />;
      case "buyer":
        return "Buyer";
      case "fabric_type":
        return "Fabric Type";
      default:
        return null;
    }
  };

  return (
    <div>
      <Form
        layout="vertical"
        form={form}
        onFinish={onFinish}
        initialValues={{
          order_status: "Waiting For Feedback",
        }}
      >
        <Card>
          <Row gutter={6}>
            <Col span={4}>
              <Form.Item label=" " name="subcontract_status" valuePropName="checked">
                <Checkbox disabled={viewMode || editMode}>Sub-Contract</Checkbox>
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                label="Inquiry Status"
                name="inquiry_status"
                rules={[
                  {
                    required: true,
                    message: "Inquiry Status is required",
                  },
                ]}
              >
                <Select
                  showSearch
                  placeholder="Select status"
                  optionFilterProp="children"
                  disabled={true} // Always disabled, defaults to Capacity Booking
                  size="small"
                  allowClear={false}
                >
                  {isArrayAndHasValue(inquiryStatus) &&
                    inquiryStatus?.map((statusItem, index) => (
                      <Option value={statusItem?.value} key={statusItem?.id || index}>
                        {statusItem?.name || ""}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                label="Inquiry No. / Style No."
                name="inquiry_no"
                rules={[
                  {
                    required: true,
                    message: "Inquiry No. is required",
                  },
                ]}
              >
                <Input placeholder="Inquiry No." size="small" disabled={viewMode} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label="Received Date" name="received_date" initialValue={moment()}>
                <CustomDatePicker align="right" size="small" disabled={viewMode} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label="Request Date" name="request_date" initialValue={moment()}>
                <CustomDatePicker align="right" size="small" disabled={true} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item name="received_by" hidden />
              <Form.Item label="Merchandiser" name="received_by_name">
                <Select
                  showSearch
                  placeholder="Select user"
                  optionFilterProp="children"
                  disabled={viewMode}
                  size="small"
                  onSearch={(value) => getAllUsers({ name: value })}
                  allowClear
                  onSelect={(value) => {
                    form.setFieldsValue({
                      received_by: value,
                    });
                  }}
                >
                  {isArrayAndHasValue(users) &&
                    users?.map((userItem, index) => (
                      <Option value={userItem?.id} key={userItem?.id || index}>
                        {userItem?.name || ""}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
            {subcontract_status ? (
              <>
                <Col span={4}>
                  <Form.Item label="Factory Name" name="sub_contract_factory_id">
                    <Select
                      showSearch
                      placeholder="Select a Factory Name"
                      disabled={viewMode}
                      allowClear
                      size="small"
                      onChange={(value) => {
                        const selectedFactory = subcontractStatusOption.find((item) => item.id === value);
                        form.setFieldsValue({
                          address: selectedFactory?.address,
                        });
                      }}
                    >
                      {isArrayAndHasValue(subcontractStatusOption) &&
                        subcontractStatusOption.map((sup) => (
                          <Option value={sup?.id} key={sup?.id}>
                            {sup.name}
                          </Option>
                        ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item label="Address" name="address">
                    <Input placeholder="Address" size="small" disabled />
                  </Form.Item>
                </Col>
              </>
            ) : (
              <>
                <Col span={4}>
                  <Row gutter={10}>
                    <Col span={20}>
                      <Form.Item
                        label="Buying office"
                        name="buying_office_id"
                        rules={[
                          {
                            required: true,
                            message: `Buying office is required `,
                          },
                        ]}
                      >
                        <Select
                          showSearch
                          style={{
                            width: "100%",
                          }}
                          placeholder="Select a Buying office"
                          onChange={(value) => {
                            if (value) {
                              getBuyersList(value);
                              setSelectedBuyingOffice(value);
                            } else {
                              form.resetFields(["buyer_id", "brand_id"]);
                              setBuyer([]);
                              setBrand([]);
                              setSelectedBuyingOffice(null);
                            }
                          }}
                          optionFilterProp="children"
                          disabled={viewMode}
                          size="small"
                          onSearch={(value) => {
                            getBuyingOfficeList({ name: value });
                          }}
                          allowClear
                          onClear={() => getBuyingOfficeList()}
                        >
                          {isArrayAndHasValue(buyingOffice) &&
                            buyingOffice.map((sup) => (
                              <Option value={sup?.id} key={sup?.id}>
                                {sup?.name}
                              </Option>
                            ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={4}>
                      <div className="centered-item-parent">
                        <Form.Item label=" ">
                          <Button
                            icon={<PlusOutlined />}
                            size="small"
                            onClick={() => onModalOpen({ modalName: "buying_office" })}
                          />
                        </Form.Item>
                      </div>
                    </Col>
                  </Row>
                </Col>
                <Col span={4}>
                  <Form.Item name="buyer_short_name" hidden />
                  <Form.Item label="Buyer" name="buyer_id" rules={[{ required: true, message: `Buyer is required ` }]}>
                    <Select
                      showSearch
                      placeholder="Select a Buyer"
                      onChange={(value) => {
                        if (value) {
                          getBrand(value);
                          setSelectedBuyer(value);
                        } else {
                          form.resetFields(["brand_id"]);
                          setBrand([]);
                          setSelectedBuyer(null);
                        }
                      }}
                      optionFilterProp="children"
                      disabled={viewMode}
                      size="small"
                      onSearch={(value) => getBuyersList(selectedBuyingOffice, { name: value })}
                      allowClear
                      onClear={() => getBuyersList(selectedBuyingOffice)}
                    >
                      {isArrayAndHasValue(buyer) &&
                        buyer.map((buy) => (
                          <Option value={buy?.id} key={buy?.id}>
                            {buy?.name}
                          </Option>
                        ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item label="Brand" name="brand_id">
                    <Select
                      showSearch
                      placeholder="Select a Brand"
                      optionFilterProp="children"
                      onSearch={(value) => getBrand(selectedBuyer, { name: value })}
                      disabled={viewMode}
                      size="small"
                      allowClear
                      onClear={() => setBrand([])}
                    >
                      {brand?.length &&
                        brand?.map((pi) => (
                          <Option value={pi?.id} key={pi?.id}>
                            {pi?.name}
                          </Option>
                        ))}
                    </Select>
                  </Form.Item>
                </Col>
              </>
            )}
            <Col span={4}>
              <Form.Item label="Garments Category" name="garment_category_id">
                <Select
                  showSearch
                  placeholder="Select a category"
                  optionFilterProp="children"
                  size="small"
                  disabled={viewMode}
                  onSearch={(value) => getAllGarmentCategory({ name: value })}
                  allowClear
                >
                  {isArrayAndHasValue(garmentCategoryList) &&
                    garmentCategoryList?.map((categoryItem, index) => (
                      <Option value={categoryItem?.id} key={categoryItem?.id || index}>
                        {categoryItem?.name || ""}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label="Garments Type" name="garment_type_id">
                <Select
                  showSearch
                  placeholder="Select a type"
                  optionFilterProp="children"
                  size="small"
                  disabled={viewMode}
                  onSearch={(value) => getAllGarmentType({ name: value })}
                  allowClear
                >
                  {isArrayAndHasValue(garmentTypeList) &&
                    garmentTypeList?.map((typeItem, index) => (
                      <Option value={typeItem?.id} key={typeItem?.id || index}>
                        {typeItem?.name || ""}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label="Garments For" name="garment_for_id">
                <Select
                  showSearch
                  placeholder="Select for"
                  optionFilterProp="children"
                  size="small"
                  onSearch={(value) => getAllGarmentFor({ name: value })}
                  allowClear
                  disabled={viewMode}
                >
                  {isArrayAndHasValue(garmentForList) &&
                    garmentForList?.map((typeItem, index) => (
                      <Option value={typeItem?.id} key={typeItem?.id || index}>
                        {typeItem?.name || ""}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                label="Order Qty."
                name="order_quantity"
                rules={[
                  {
                    required: true,
                    message: `Order Qty. is required `,
                  },
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  size="small"
                  placeholder="Order Qty."
                  disabled={viewMode}
                  formatter={(value) => commaSeparateNumber(value)}
                  onChange={() => {
                    // Trigger form validation to recalculate expected_order_quantity
                    setTimeout(() => {
                      const orderQty = form.getFieldValue("order_quantity");
                      const percentage = form.getFieldValue("extra_percent") || 0;
                      const expectedQty = orderQty ? orderQty + (orderQty * percentage) / 100 : 0;
                      form.setFieldsValue({
                        expected_order_quantity: expectedQty,
                      });
                    }, 0);
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label="Cutting (%)" name="extra_percent">
                <InputNumber
                  style={{ width: "100%" }}
                  size="small"
                  placeholder="Cutting (%)"
                  disabled={viewMode}
                  min={0}
                  max={100}
                  onChange={() => {
                    // Trigger form validation to recalculate expected_order_quantity
                    setTimeout(() => {
                      const orderQty = form.getFieldValue("order_quantity");
                      const percentage = form.getFieldValue("extra_percent") || 0;
                      const expectedQty = orderQty ? orderQty + (orderQty * percentage) / 100 : 0;
                      form.setFieldsValue({
                        expected_order_quantity: expectedQty,
                      });
                    }, 0);
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label="Exp. Order Qty. (Calculated)" name="expected_order_quantity">
                <InputNumber
                  style={{ width: "100%" }}
                  size="small"
                  placeholder="Calculated Qty."
                  disabled={true}
                  formatter={(value) => commaSeparateNumber(value)}
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label="Exp. Delivery Date" name="expected_delivery_date">
                <CustomDatePicker align="right" size="small" disabled={viewMode} />
              </Form.Item>
            </Col>

            <Col span={4}>
              <Form.Item
                label="Production Month From"
                name="production_month_from"
                rules={[
                  {
                    required: inquiry_status_watch === "3",
                    message: "Production Month From is required for Capacity Booking",
                  },
                ]}
              >
                <CustomDatePicker
                  align="right"
                  size="small"
                  disabled={viewMode}
                  // picker="month"
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                label="Production Month To"
                name="production_month_to"
                rules={[
                  {
                    required: inquiry_status_watch === "3",
                    message: "Production Month To is required for Capacity Booking",
                  },
                ]}
              >
                <CustomDatePicker
                  align="right"
                  size="small"
                  disabled={viewMode}
                  // picker="month"
                />
              </Form.Item>
            </Col>

            <Col span={4}>
              <Form.Item
                label="Description"
                name="description"
                rules={[{ required: true, message: `Description is required ` }]}
              >
                <TextArea size="small" placeholder="Description" disabled={viewMode} />
              </Form.Item>
            </Col>

            <Col span={4}>
              <Form.Item label="Season" name="season">
                <Input style={{ width: "100%" }} size="small" placeholder="Season" disabled={viewMode} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label="Size Range" name="size_range">
                <Input style={{ width: "100%" }} size="small" placeholder="Size Range" disabled={viewMode} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label="Order Type" name="order_type">
                <Select
                  showSearch
                  placeholder="Select order type"
                  optionFilterProp="children"
                  disabled={viewMode}
                  size="small"
                  allowClear
                >
                  {isArrayAndHasValue(ORDER_TYPE) &&
                    ORDER_TYPE?.map((item, index) => (
                      <Option value={item?.value} key={item?.id || index}>
                        {item?.label || ""}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label="Order Channel" name="order_channel">
                <Select
                  showSearch
                  placeholder="Select order channel"
                  optionFilterProp="children"
                  disabled={viewMode}
                  size="small"
                  allowClear
                >
                  {isArrayAndHasValue(ORDER_CHANNEL) &&
                    ORDER_CHANNEL?.map((item, index) => (
                      <Option value={item?.value} key={item?.id || index}>
                        {item?.label || ""}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label={`Fabric ${subcontract_status ? "Content" : "Type"}`} name="fabric_content">
                <Select
                  showSearch
                  placeholder={`Select Fabric ${subcontract_status ? "Content" : "Type"}`}
                  optionFilterProp="children"
                  disabled={viewMode}
                  size="small"
                  allowClear
                >
                  {isArrayAndHasValue(FABRIC_TYPE) &&
                    FABRIC_TYPE?.map((item, index) => (
                      <Option value={item?.value} key={item?.id || index}>
                        {item?.label || ""}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={subcontract_status ? 0 : 4}>
              <Form.Item label="FOB $" name="fob" hidden={subcontract_status}>
                <InputNumber
                  style={{ width: "100%" }}
                  size="small"
                  placeholder="FOB"
                  disabled={viewMode}
                  formatter={(value) => commaSeparateNumber(value)}
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                label="Capacity Availability"
                name="capacity_availability"
                rules={[
                  {
                    required: false,
                    message: `Capacity Availability is required `,
                  },
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  size="small"
                  placeholder="Capacity Availability"
                  disabled={viewMode}
                  formatter={(value) => commaSeparateNumber(value)}
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label="Country" name="country_id">
                <Select
                  showSearch
                  placeholder="Select Country"
                  optionFilterProp="children"
                  disabled={viewMode}
                  size="small"
                  allowClear
                  onSearch={(value) => getCountries({ name: value })}
                  onClear={() => getCountries()}
                  filterOption={false}
                >
                  {isArrayAndHasValue(countryList) &&
                    countryList?.map((item) => (
                      <Option value={item?.id} key={item?.id}>
                        {item?.name || ""}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={subcontract_status ? 4 : 0}>
              <Form.Item label="Fabric Status" name="fabric_status" hidden={!subcontract_status}>
                <Input style={{ width: "100%" }} size="small" placeholder="Fabric Status" disabled={viewMode} />
              </Form.Item>
            </Col>
            <Col span={subcontract_status ? 4 : 0}>
              <Form.Item label="Trims Status" name="trim_status" hidden={!subcontract_status}>
                <Input style={{ width: "100%" }} size="small" placeholder="Trims Status" disabled={viewMode} />
              </Form.Item>
            </Col>
            <Col span={subcontract_status ? 4 : 0}>
              <Form.Item label="Accessories Status" name="accessories_status" hidden={!subcontract_status}>
                <Input style={{ width: "100%" }} size="small" placeholder="Accessories Status" disabled={viewMode} />
              </Form.Item>
            </Col>

            <Col span={4}>
              <Form.Item
                name="embroidery"
                placeholder="Embroidery"
                label="Embroidery"
                valuePropName="checked"
                initialValue={false}
              >
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" disabled={viewMode} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                name="printing"
                placeholder="Printing"
                label="Printing"
                valuePropName="checked"
                initialValue={false}
              >
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" disabled={viewMode} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                name="washing"
                placeholder="Washing"
                label="Washing"
                valuePropName="checked"
                initialValue={false}
              >
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" disabled={viewMode} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                name="smoking"
                placeholder="Smocking"
                label="Smocking"
                valuePropName="checked"
                initialValue={false}
              >
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" disabled={viewMode} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                name="pintuck"
                placeholder="Pintuck"
                label="Pintuck"
                valuePropName="checked"
                initialValue={false}
              >
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" disabled={viewMode} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                name="snap_button"
                placeholder="Snap Button"
                label="Snap Button"
                valuePropName="checked"
                initialValue={false}
              >
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" disabled={viewMode} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Reference
          inquiryReferences={inquiryReferences}
          setInquiryReferences={setInquiryReferences}
          viewMode={viewMode}
        />

        <SpecialProcessTable view={true} specialProcessList={specialProcessList} />

        <MachineRentTable view={true} machineRentList={machineRentList} />

        <DocumentUpload
          DOC_ADD={STYLE_ADD_DOC}
          setFileList={setFileList}
          filesList={filesList}
          view={viewMode}
          header={!viewMode ? "Attachment" : null}
          preview={true}
          showType={true}
          showReference={true}
        />

        {Array.isArray(documents) && documents.length > 0 && (
          <DocumentUpload filesList={documents} view={true} preview={true} showType={true} showReference={true} />
        )}

        {isEdit && (
          <>
            <OBForm editInfo={editInfo} />
            <CMForm CMInfo={CMInfo} inquiryId={inquiryId} viewMode={viewMode} />
            <OrderConfirmationForm form={form} viewMode={viewMode} />
          </>
        )}
      </Form>

      <Modal
        open={isModalOpen}
        title={`Create ${renderModalTitle()}`}
        onCancel={() => {
          onCloseModal();
        }}
        maskStyle={{ backdropFilter: "blur(4px)" }}
        footer={null}
      >
        {renderModalUI()}
      </Modal>
    </div>
  );
});

export default BuyerInquiryForm;
